import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../../store/slices/authSlice";
import { LogOut, X, ChevronDown } from "lucide-react";
import { FaFacebookF, FaLinkedin, FaInstagram, FaDiscord, FaTwitter } from "react-icons/fa6";
import { getStaticUrl } from "../../config/domains";

const Sidebar = ({ activeMenu, setActiveMenu, isOpen, isMobile = false, onClose, menuItems = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sidebarRef = useRef(null);

  // Track which accordion groups are expanded
  const [expandedGroups, setExpandedGroups] = useState({});

  // Auto-expand the group that contains the active menu item
  useEffect(() => {
    if (!menuItems) return;
    menuItems.forEach((item) => {
      if (item.children) {
        const hasActive = item.children.some((child) => child.id === activeMenu);
        if (hasActive) {
          setExpandedGroups((prev) => ({ ...prev, [item.id]: true }));
        }
      }
    });
  }, [activeMenu, menuItems]);

  // Close sidebar/drawer when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        onClose && onClose();
      }
    };

    if (isMobile && isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, isOpen, onClose]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    if (isMobile) {
      onClose && onClose();
    }
  };

  const handleMenuClick = (itemId, navigatePath) => {
    setActiveMenu(itemId);
    if (navigatePath) {
      navigate(navigatePath);
    }
    if (isMobile) {
      onClose && onClose();
    }
  };

  const toggleGroup = (groupId) => {
    // Only allow expanding when sidebar is open
    if (!isOpen && !isMobile) return;
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const isGroupActive = (item) => {
    if (!item.children) return false;
    return item.children.some((child) => child.id === activeMenu);
  };

  // Get menu items based on role or use passed menuItems
  const finalMenuItems = menuItems;

  const renderMenuItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedGroups[item.id];
    const groupActive = isGroupActive(item);

    if (hasChildren) {
      return (
        <div key={item.id}>
          {/* Group header */}
          <div
            className={`flex items-center gap-3 px-3 py-2 rounded-2xl transition-all duration-200 cursor-pointer ${groupActive
              ? "bg-[#F4F8F7] text-gray-900 border border-[#E0E4E2] rounded-2xl"
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            onClick={() => toggleGroup(item.id)}
            title={item.label}
          >
            <div className="flex-shrink-0">{item.icon}</div>
            <span
              className={`font-medium truncate transition-all duration-300 ${isOpen || isMobile ? "opacity-100" : "opacity-0 hidden w-0"
                }`}
            >
              {item.label}
            </span>
            {item.badge && (isOpen || isMobile) && (
              <span className="px-3 py-0.5 text-xs font-normal text-white rounded-full bg-gradient-to-r from-[#2E7D5B] via-[#5D8D55] to-[#E5BB53] flex-shrink-0">
                {item.badge}
              </span>
            )}
            {(isOpen || isMobile) && (
              <ChevronDown
                className={`w-4 h-4 flex-shrink-0 ml-auto transition-transform duration-300 ${isExpanded ? "rotate-180" : ""
                  }`}
              />
            )}
          </div>

          {/* Children (accordion) */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded && (isOpen || isMobile) ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
              }`}
          >
            <div className="mt-1 space-y-1">
              {item.children.map((child) => (
                <div
                  key={child.id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-2xl transition-all duration-200 cursor-pointer ${activeMenu === child.id
                    ? "bg-[#F4F8F7] text-gray-900 border border-[#E0E4E2] rounded-2xl"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  onClick={() => handleMenuClick(child.id, child.navigate || child.path)}
                  title={child.label}
                >
                  <div className="flex-shrink-0">{child.icon}</div>
                  <span className="font-medium truncate">{child.label}</span>
                  {child.badge && (
                    <span className="px-3 py-0.5 text-xs font-normal text-white rounded-full bg-gradient-to-r from-[#2E7D5B] via-[#5D8D55] to-[#E5BB53] flex-shrink-0 ml-auto">
                      {child.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={item.id}
        className={`flex items-center gap-3 px-3 py-2 rounded-2xl transition-all duration-200 cursor-pointer ${activeMenu === item.id
          ? "bg-[#F4F8F7] text-gray-900 border border-[#E0E4E2] rounded-2xl"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 "
          }`}
        onClick={() => handleMenuClick(item.id, item.navigate || item.path)}
        title={item.label}
      >
        <div className="flex-shrink-0">{item.icon}</div>
        <span
          className={`font-medium truncate transition-all duration-300 ${isOpen || isMobile ? "opacity-100" : "opacity-0 hidden w-0"
            }`}
        >
          {item.label}
        </span>
        {item.badge && (isOpen || isMobile) && (
          <span className="px-3 py-0.5 text-xs font-normal text-white rounded-full bg-gradient-to-r from-[#2E7D5B] via-[#5D8D55] to-[#E5BB53] flex-shrink-0 ml-auto">
            {item.badge}
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      ref={sidebarRef}
      className={`
        h-full bg-white shadow-lg transition-all duration-300 ease-in-out z-40
        ${isMobile ? "w-72" : isOpen ? "w-72" : "w-16"}
      `}
    >
      <div className={`h-full flex flex-col ${!isOpen && !isMobile ? "" : "p-6"}`}>
        {/* Logo Section */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex-1 w-auto h-full flex items-center justify-center flex-shrink-0">
            {isOpen || isMobile ? (
              <img
                src="/images/Nkenne Ai-01 2.png"
                alt="NKENNE AI"
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                src="/Nkenne.svg"
                alt="NKENNE"
                className="w-[30px] h-full object-contain"
              />
            )}
          </div>
          {isMobile && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="space-y-1 overflow-y-auto flex-1">
          {finalMenuItems.map((item) => renderMenuItem(item))}
        </nav>

        {/* Footer Links */}
        <div className="mt-auto pt-8">
          <div className={`space-y-2 mb-6 ${!isOpen && !isMobile ? "hidden" : ""}`}>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <a href={getStaticUrl("/about")} target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-gray-700 transition-colors">About Us</a>
              <a href={getStaticUrl("/pricing")} target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-gray-700 transition-colors">Pricing</a>
              <a href={getStaticUrl("/playground")} target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-gray-700 transition-colors">Playground</a>
              {/* <a href={getStaticUrl("/blog")} target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-gray-700 transition-colors">Blog</a> */}
              <a href={getStaticUrl("/terms-of-service")} target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-gray-700 transition-colors">Terms</a>
              <a href={getStaticUrl("/privacy-policy")} target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-gray-700 transition-colors">Privacy</a>
              <a href={getStaticUrl("/support")} target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-gray-700 transition-colors">Support</a>
            </div>

            <div className="flex items-center gap-2 mt-4 py-2 border-b border-gray-100">
              <a href="https://www.facebook.com/nkenneapp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#E9F3F1] flex items-center justify-center hover:bg-[#DDEAE7] transition-all duration-300 group" title="Facebook">
                <FaFacebookF className="w-4 h-4 text-secondary group-hover:scale-110 transition-transform" />
              </a>
              <a href="https://www.linkedin.com/company/nkenneai" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#E9F3F1] flex items-center justify-center hover:bg-[#DDEAE7] transition-all duration-300 group" title="LinkedIn">
                <FaLinkedin className="w-4 h-4 text-secondary group-hover:scale-110 transition-transform" />
              </a>
              <a href="https://www.instagram.com/nkenneapp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#E9F3F1] flex items-center justify-center hover:bg-[#DDEAE7] transition-all duration-300 group" title="Instagram">
                <FaInstagram className="w-4 h-4 text-secondary group-hover:scale-110 transition-transform" />
              </a>
              <a href="https://twitter.com/NKENNEapp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#E9F3F1] flex items-center justify-center hover:bg-[#DDEAE7] transition-all duration-300 group" title="Twitter">
                <FaTwitter className="w-4 h-4 text-secondary group-hover:scale-110 transition-transform" />
              </a>
              <a href="https://discord.gg/9qVN3JjqgX" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#E9F3F1] flex items-center justify-center hover:bg-[#DDEAE7] transition-all duration-300 group" title="Discord">
                <FaDiscord className="w-4 h-4 text-secondary group-hover:scale-110 transition-transform" />
              </a>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-3 w-full text-left text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all duration-200`}
            title={!isOpen && !isMobile ? "Logout" : ""}
          >
            <div className="flex-shrink-0">
              <LogOut className="w-5 h-5" />
            </div>
            <span
              className={`font-medium whitespace-nowrap transition-all duration-300 ${isOpen || isMobile ? "opacity-100" : "opacity-0 hidden"
                }`}
            >
              Logout
            </span>
          </button>
        </div>
      </div >
    </div >
  );
};

export default Sidebar;